import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, UntypedFormGroup } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TextmultilineComponent } from "@components/textmultiline/textmultiline.component";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { from } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { DirtyFormService, IDirtyFormResult } from "~app/common/services/dirty-form.service";
import { DateUtils } from "~app/common/utils/date-utils";
import { SaveCKLeverantorCommand } from "../../commads/SaveCKLeverantorCommand";
import { ICKLeverantorDetail } from "../../models/ckLeverantorDetail";
import { CKLeverantorDetailsViewModel } from "../../models/ckLeverantorDetailsViewModel";

@Component({
    selector: "rask-ckleverantor-details-dialog",
    templateUrl: "./ckleverantor-details-dialog.component.html",
    styleUrls: ["./ckleverantor-details-dialog.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TextmultilineComponent,
        CheckboxComponent,
        FormsModule,
        SelectorComponent,
        SaveButtonComponent
    ]
})
export class CkleverantorDetailsDialogComponent implements OnInit {
    activeModal = inject(NgbActiveModal);
    canEdit = false;
    ckLeverantorDetails: CKLeverantorDetailsViewModel;
    data: ICKLeverantorDetail;
    dataChanged = false;
    form: UntypedFormGroup;
    initialized = false;

    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly dirtyForm = inject(DirtyFormService);
    private readonly toasterService = inject(ToastService);

    get koppladeCkLeverantorer() {
        return this.ckLeverantorDetails.koppladeCkLeverantorer.map(x => `${x.huvudkod} - ${x.internnummer}`).join(", ");
    }

    canSave() {
        if (this.form.dirty || this.dataChanged) {
            // eslint-disable-next-line sonarjs/prefer-single-boolean-return
            if (this.form.dirty && !this.form.valid) {
                return false;
            }
            return true;
        } else {
            return false;
        }
    }

    ckLeverantorFormatter = item => item ? `${item.huvudkod} - ${item.internnummer}` : undefined;

    dirtyCheckCallback(result: IDirtyFormResult): void {
        if (result.isSkipSave) {
            this.activeModal.dismiss();
        }
        if (result.isSave) {
            this.activeModal.dismiss();
        }
    }

    disableDoljCKLeverantor(): boolean {
        return this.ckLeverantorDetails.koppladeCkLeverantorer.length !== 0 || this.ckLeverantorDetails.koppladTillLeverantorsorder;
    }

    dismiss() {
        if (this.dirtyForm.canClose()) {
            this.activeModal.dismiss();
        }
    }

    idFormatter = (value: ICKLeverantorDetail) => value ? value.id : undefined;

    isModalDirty(): boolean {
        return this.dataChanged;
    }

    ngOnInit(): void {
        this.formatData();
        this.form = new UntypedFormGroup({});
        this.canEdit = this.authenticationService.canEdit();

        this.dirtyForm.onDirtyNgModel(
            this.isModalDirty.bind(this),
            this.save.bind(this),
            d => this.dirtyCheckCallback(d),
            () => this.canSave(),
            "Osparade förändringar",
            "Om du lämnar så kommer förändringarna inte sparas",
            { size: "lg",
                centered: false });

        this.initialized = true;
    }

    onChange() {
        this.dataChanged = true;
    }

    onDoldChange(value: any) {
        this.dataChanged = true;
        if(!value) {
            this.data.parentCKLeverantorId = undefined;
        }
    }

    save() {
        if (!this.canSave()) {
            return;
        }
        this.commandService.addTransactionCommand(new SaveCKLeverantorCommand(
            this.data.id,
            this.data.doljForLeverantorsorder,
            this.data.parentCKLeverantorId));

        const work = from(this.commandService.pushCommandStack());

        this.busyService.addWork(work)
            .subscribe(res => {
                if (res) {
                    this.toasterService.add("DEFAULT_CHANGE_SUCCESS");
                    this.dataChanged = false;
                }
            });

        this.activeModal.dismiss();
    }

    private formatData() {
        this.ckLeverantorDetails = {
            id: this.data.id,
            aktiv: this.data.aktiv,
            uppdaterad: this.data.receiveDateTime ? DateUtils.formatDateString(this.data.receiveDateTime, DateUtils.datetimeformat) : "",
            statusILime: (this.data && this.data?.aktivLime) ? "Aktiv i Lime" : "Inaktiv i Lime",
            besoksadress: [
                this.data.besoksadress,
                this.data.besoksadress2,
                this.data.besokPostnummer,
                this.data.besokPostortadress
            ],
            postadress: [
                this.data.adress,
                this.data.adress2,
                this.data.postnummer,
                this.data.postort
            ],
            avtalsansvarig: this.data.avtalsansvarig,
            avdelning: this.data.avdelning,
            limeId: this.data.limeId,
            huvudkod: this.data.huvudkod,
            internnummer: this.data.internnummer,
            telefon: this.data.telefon,
            orgNummer: this.data.orgNummer,
            landOchLandskod: `${this.data.land} ${this.data.landISO}`,
            doljForLeverantorsorder: this.data.doljForLeverantorsorder,
            valbaraForaldraCkLeverantorer: this.data.valbaraForaldraCkLeverantorer,
            parentCKLeverantorId: this.data.parentCKLeverantorId,
            koppladeCkLeverantorer: this.data.koppladeCkLeverantorer,
            koppladTillLeverantorsorder: this.data.koppladTillLeverantorsorder
        };
    }
}
